import posthog from 'posthog-js';
import { JsonType } from 'posthog-js';
import { computed, ComputedRef, Ref, ref, watch } from 'vue';
import { isLocalFeatureFlagEnabled, usesLocalFeatureFlags } from '@/composables/localFeatureFlags';
import { isPostHogConfigured } from '@/plugins/posthog';

export enum FeatureFlag {
    ScanWorkflowUI = 'handheld-ui',
    BrowserZplLabels = 'browser-zpl-labels',
    FnskuReceiving = 'fnsku-receiving',
    WalmartChannel = 'walmart-channel',
    PlatformOauthManagement = 'platform-oauth-management',
}

export enum EarlyAccessFlag {
    Placeholder = 'placeholder',
}

export const FLAG_LOAD_TIMEOUT_MS = 10_000;

// Local resolution (staging) answers every flag at once; see localFeatureFlags.ts.
const isLocallyEnabled = (flag: FeatureFlag): boolean => isLocalFeatureFlagEnabled(flag);

// isEnabled() answers undefined until PostHog has loaded the flags and is a plain function
// call, so watching it directly would never re-run. This ref is the one reactive signal a
// caller can wait on before picking a code path off a flag.
//
// It latches deliberately. A caller that has already rendered one of two branches is
// showing the user an interactive form, and flipping it on a later payload would swap that
// form out mid-entry, discarding what they typed. So the wait has to be settled once, and
// settled with a real answer wherever one is possible: when PostHog is not configured
// nothing will ever load and the answer is immediately "flags off", and when it is
// configured the timeout is long enough that a slow-but-successful response still wins,
// leaving it as a backstop for a request that failed outright.
const flagsSettled = ref(false);
let subscribed = false;

// flagsSettled latches on the first load, and on a fresh visit that can be the anonymous load
// that lands before the layout identifies the user. A caller that only shows or hides a
// control can safely follow every later load instead, so each one is counted here.
const flagLoads = ref(0);

const subscribeToFlagLoad = (): Ref<boolean> => {
    if (subscribed) return flagsSettled;

    subscribed = true;

    if (usesLocalFeatureFlags() || !isPostHogConfigured()) {
        flagsSettled.value = true;

        return flagsSettled;
    }

    posthog.onFeatureFlags(() => {
        flagsSettled.value = true;
        flagLoads.value++;
    });
    window.setTimeout(() => {
        flagsSettled.value = true;
    }, FLAG_LOAD_TIMEOUT_MS);

    return flagsSettled;
};

const waitForFlagsSettled = (): Promise<void> => {
    const settled = subscribeToFlagLoad();
    if (settled.value) return Promise.resolve();

    return new Promise((resolve) => {
        const stopWatch = watch(settled, (isSettled) => {
            if (isSettled) {
                stopWatch();
                resolve();
            }
        });
    });
};

// On only once PostHog has loaded flags and the latest load turned this one on. Unlike the
// latched flagsSettled, this follows the identified user's flags when they replace anonymous ones.
const followFlag = (flag: FeatureFlag): ComputedRef<boolean> => {
    subscribeToFlagLoad();

    if (usesLocalFeatureFlags()) return computed(() => isLocallyEnabled(flag));

    return computed(() => flagLoads.value > 0 && posthog.isFeatureEnabled(flag) === true);
};

export default function useFeatureFlags(): { isEnabled: (flag: FeatureFlag) => boolean | undefined; followFlag: (flag: FeatureFlag) => ComputedRef<boolean>; flagsSettled: Ref<boolean>; waitForFlagsSettled: () => Promise<void>; getVariantKey: (flag: FeatureFlag) => string | boolean | undefined; getPayload: (flag: FeatureFlag) => JsonType; enrollInEarlyAccess: (flag: EarlyAccessFlag) => void; unenrollFromEarlyAccess: (flag: EarlyAccessFlag) => void; } {
    return {
        isEnabled: (flag: FeatureFlag): boolean | undefined => (usesLocalFeatureFlags() ? isLocallyEnabled(flag) : posthog.isFeatureEnabled(flag)),
        followFlag,
        flagsSettled: subscribeToFlagLoad(),
        waitForFlagsSettled,
        getVariantKey: (flag: FeatureFlag): string | boolean | undefined => (usesLocalFeatureFlags() ? isLocallyEnabled(flag) : posthog.getFeatureFlag(flag)),
        getPayload: (flag: FeatureFlag): JsonType => (usesLocalFeatureFlags() ? null : posthog.getFeatureFlagPayload(flag)),
        enrollInEarlyAccess: (flag: EarlyAccessFlag): void => {
            posthog.updateEarlyAccessFeatureEnrollment(flag, true);
            window.location.reload();
        },
        unenrollFromEarlyAccess: (flag: EarlyAccessFlag): void => {
            posthog.updateEarlyAccessFeatureEnrollment(flag, false);
            window.location.reload();
        },
    };
}
